import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useLogin } from '@/hooks/useAuth';
import { useBusinessFooter } from '@/hooks/useRankings';
import { prefetchDashboard } from '@/hooks/useDashboardQueries';
import { ApiError, HttpError } from '@/api/client';
import type { LoginBody } from '@/api/auth';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  TrendingUp,
  Settings,
  Users,
  Truck,
  AlertCircle,
  Loader2,
} from 'lucide-react';

const FEATURES = [
  {
    icon: TrendingUp,
    title: 'Real-time Insights',
    desc: 'Track orders and business growth',
  },
  {
    icon: Settings,
    title: 'Complete Control',
    desc: 'Stores, zones and settings',
  },
  {
    icon: Users,
    title: 'Manage Your Team',
    desc: 'Customers, deliverymen & staff',
  },
  {
    icon: Truck,
    title: 'Faster Operations',
    desc: 'Streamline your workflow',
  },
] as const;

export function LoginPage() {
  const [email, setEmail] = useState('admin@ezzo.local');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const login = useLogin();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: footer } = useBusinessFooter();

  // Production may store the account as super-admin (role_id=1) or admin employee.
  // Prefer admin; fall back to admin_employee when Laravel reports the email is not a super-admin.
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (login.isPending || submitting) return;
    setFormError(null);
    setSubmitting(true);
    try {
      let role: LoginBody['role'] = 'admin';
      try {
        await login.mutateAsync({ email, password, role, remember });
      } catch (err) {
        const fe = err instanceof ApiError || err instanceof HttpError ? err : null;
        const emailIssue = fe?.errors?.email?.[0]?.toLowerCase().includes('email does not match');
        if (!emailIssue) throw err;
        role = 'admin_employee';
        await login.mutateAsync({ email, password, role, remember });
      }
      // Warm dashboard queries while navigating so first paint is instant.
      void prefetchDashboard(queryClient, {
        zone_id: 'all',
        statistics_type: 'this_year',
        commission_overview: 'this_year',
        user_overview: 'this_year',
      });
      void navigate({ to: '/dashboard' as never });
    } catch (err) {
      if (err instanceof ApiError) setFormError(err);
      else setFormError(new ApiError(500, 'Unable to sign in. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const busy = login.isPending || submitting;
  const err = formError ?? (login.error instanceof ApiError ? login.error : null);
  const fieldError = err?.errors?.email?.[0] || err?.errors?.password?.[0];
  const generalError = err && !fieldError ? err.message : null;
  const version = footer?.softwareVersion || '3.6';

  return (
    <div className="login-page">
      <section className="login-hero" aria-hidden="true">
        <div
          className="login-hero-bg"
          style={{ backgroundImage: 'url(/images/login-hero.png)' }}
        />
        <div className="login-hero-content">
          <div className="login-hero-brand">
            <img src="/ezzo-logo.png" alt="" className="login-hero-logo" width={140} height={40} />
            <span className="login-hero-brand-sub">FOOD DELIVERY</span>
          </div>

          <h1 className="login-hero-title">
            Manage
            <br />
            Everything
            <br />
            <span>In One Place.</span>
          </h1>

          <p className="login-hero-desc">
            Complete control over your delivery business, customers, orders and team — all from a
            single powerful dashboard.
          </p>

          <ul className="login-hero-features">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <li key={f.title} className="login-feature">
                  <span className="login-feature-icon" aria-hidden>
                    <Icon size={18} strokeWidth={2} />
                  </span>
                  <span>
                    <strong>{f.title}</strong>
                    <small>{f.desc}</small>
                  </span>
                </li>
              );
            })}
          </ul>

          <p className="login-hero-trust">Trusted by growing food businesses</p>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-version-badge">Software Version · {version}</div>

        <div className="login-panel-inner">
          <div className="login-brand-block">
            <img src="/ezzo-logo.png" alt="Ezzo" className="login-panel-logo" width={160} height={48} />
            <div className="login-panel-brand-sub">ADMIN PANEL</div>
          </div>

          <h2 className="login-title">Welcome Back!</h2>
          <p className="login-subtitle">
            Sign in to your Ezzo Admin panel to continue managing your business.
          </p>

          {generalError ? (
            <div className="login-alert" role="alert">
              <AlertCircle size={18} aria-hidden />
              <div>
                <strong>Unable to sign in.</strong>
                <span>{generalError}</span>
              </div>
            </div>
          ) : null}

          <form className="login-form" onSubmit={onSubmit} noValidate>
            <div className="login-field">
              <label htmlFor="login-email">Email Address</label>
              <div className="login-input-wrap">
                <Mail size={18} aria-hidden className="login-input-icon" />
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@ezzo.local"
                  aria-invalid={!!err?.errors?.email?.[0]}
                  aria-describedby={err?.errors?.email?.[0] ? 'login-email-error' : undefined}
                />
              </div>
              {err?.errors?.email?.[0] ? (
                <p id="login-email-error" className="login-field-error" role="alert">
                  {err.errors.email[0]}
                </p>
              ) : null}
            </div>

            <div className="login-field">
              <label htmlFor="login-password">Password</label>
              <div className="login-input-wrap">
                <Lock size={18} aria-hidden className="login-input-icon" />
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  aria-invalid={!!err?.errors?.password?.[0]}
                  aria-describedby={err?.errors?.password?.[0] ? 'login-password-error' : undefined}
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
                </button>
              </div>
              {err?.errors?.password?.[0] ? (
                <p id="login-password-error" className="login-field-error" role="alert">
                  {err.errors.password[0]}
                </p>
              ) : null}
            </div>

            <div className="login-row">
              <label className="login-checkbox">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
              <a href="/reset-password" className="login-forgot">
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              className="login-submit"
              disabled={busy}
              aria-busy={busy}
            >
              {busy ? (
                <>
                  <Loader2 size={18} className="login-spin" aria-hidden />
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} aria-hidden />
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
